import React from "react"
import { Form, useParams, useNavigate } from "react-router-dom"
import BIJSONEditor from "./JSONEditor"
import { Api } from "@/lib/api"
import {
  Alert,
  Button,
  Heading,
  Input,
  ListItem,
  Link,
  Text,
  UnorderedList,
  SimpleGrid,
  GridItem,
  VStack,
  Stack,
  AlertIcon,
  AlertTitle,
  Flex,
} from "@chakra-ui/react"
import { Datasets, DatasetSchemas, CurrentOrg } from "@/lib/store"
import { unrollError } from "@/lib/util"
import { getPlanLimits, CONTACT_SALES_URL } from "@/lib/limits"

export default function SchemaCreate() {
  const [error, setError] = React.useState(null)
  const [dataset, setDataset] = React.useState({})
  const [datasets] = Datasets.useState()
  const [datasetSchemas] = DatasetSchemas.useState()
  const { datasetId } = useParams()
  const [currentOrg] = CurrentOrg.useState()
  const [fieldCount, setFieldCount] = React.useState(0)
  React.useEffect(() => {
    setDataset(datasets[datasetId])
  }, [datasets, datasetId])

  const api = new Api()

  // Example schema
  const initialContent = {
    type: "object",
    properties: {
      name: {
        type: "string",
      },
      age: {
        type: "integer",
        minimum: 1,
        maximum: 100,
      },
    },
    required: ["name", "age"],
  }

  // Set the initial content to a pretty indented version that can be immediately submitted.
  const [jsonContent, setJsonContent] = React.useState({
    json: undefined,
    text: JSON.stringify(initialContent, null, 2),
  })

  const onChangeJSON = React.useCallback(
    (updatedContent) => {
      setJsonContent(updatedContent)
      if (updatedContent.text) {
        try {
          const parsed = JSON.parse(updatedContent.text)
          setFieldCount(Object.keys(parsed?.properties || {}).length)
        } catch (e) {
          setFieldCount(0)
        }
      }
    },
    [jsonContent],
  )

  const navigate = useNavigate()

  const handleSubmit = async (event) => {
    event.preventDefault()
    const { name, description } = event.target.elements
    const { maxSchemasPerDataset } = getPlanLimits(currentOrg)
    if (maxSchemasPerDataset !== null) {
      const schemaCount = datasetSchemas[datasetId]?.length || 0
      if (schemaCount >= maxSchemasPerDataset) {
        setError(
          `Your plan supports up to ${maxSchemasPerDataset} schema${
            maxSchemasPerDataset === 1 ? "" : "s"
          } per dataset. Upgrade to create more.`,
        )
        return
      }
    }

    let schema

    // Invalid JSON will not parse.
    try {
      schema = JSON.parse(jsonContent.text)
    } catch (e) {
      setError(
        "JSON schema is invalid. Please correct the errors before submitting.",
      )
      return
    }

    const { maxFieldsPerSchema } = getPlanLimits(currentOrg)
    if (maxFieldsPerSchema !== null) {
      const count = Object.keys(schema?.properties || {}).length
      if (count > maxFieldsPerSchema) {
        setError(
          `Schema exceeds your plan's limit of ${maxFieldsPerSchema} fields (${count} found). Upgrade your plan or contact sales at ${CONTACT_SALES_URL}.`,
        )
        return
      }
    }

    try {
      await api.schemasCreate(
        dataset.url,
        name.value,
        description.value,
        schema,
      )
      setError(null)
      await api.schemasList()
      navigate(`/datasets/${datasetId}`)
    } catch (e) {
      console.log("Failed to create schema", e)
      setError(await unrollError(e))
    }
  }

  return (
    <Flex
      flexDirection="column"
      alignItems={"left"}
      mx={{ base: 4, md: 8, lg: 8 }}
      height="100%"
      minHeight={0}
      borderRadius="1em"
      overflowY="hidden"
    >
      <Heading variant="standardMicro" mt={0}>
        Add a new Schema
      </Heading>
      <Form
        onSubmit={handleSubmit}
        style={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <SimpleGrid
          columns={{ sm: 3, md: 3, lg: 3 }}
          gap={{ base: 1, md: 2, lg: 4 }}
          height="1px"
          flex="1 1 0"
          minHeight={0}
        >
          <GridItem colSpan={{ sm: 3, md: 1, lg: 1 }}>
            <VStack gap={{ base: 1, md: 2, lg: 4 }}>
              <VStack
                gap="0"
                alignItems={"left"}
                justifyContent="left"
                width="100%"
              >
                <Input
                  size={{ base: "sm", md: "sm", lg: "md" }}
                  variant="standardMdl"
                  name="name"
                  placeholder="Enter a name for your schema"
                  id="fld-schema-name"
                  isRequired
                />
                <Text color="black" fontSize="xs" ml={4}>
                  Human-readable name
                </Text>
              </VStack>
              <VStack
                gap="0"
                alignItems={"left"}
                justifyContent="left"
                width="100%"
              >
                <Input
                  size={{ base: "sm", md: "sm", lg: "md" }}
                  variant="standardMdl"
                  name="description"
                  placeholder="Description"
                  id="fld-schema-description"
                />
                <Text color="black" fontSize="xs" ml={4}>
                  Brief description
                </Text>
              </VStack>
            </VStack>
            {error && <Alert status="error">{error}</Alert>}
          </GridItem>
          <GridItem
            colSpan={{ sm: 3, md: 2, lg: 2 }}
            display="flex"
            flexDirection="column"
            minHeight={0}
          >
            <BIJSONEditor
              content={jsonContent}
              onChange={onChangeJSON}
              readOnly={false}
              mode="text"
              mainMenuBar={false}
              id="fld-schema-content"
            />
            {(() => {
              const { maxFieldsPerSchema } = getPlanLimits(currentOrg)
              return (
                maxFieldsPerSchema !== null &&
                fieldCount >= maxFieldsPerSchema && (
                  <Alert status="warning" mt={2}>
                    <AlertIcon />
                    <Text fontSize="sm">
                      Field limit reached ({fieldCount}/{maxFieldsPerSchema}).{" "}
                      <Link
                        href={CONTACT_SALES_URL}
                        isExternal
                        textDecoration="underline"
                      >
                        Upgrade your plan
                      </Link>{" "}
                      to add more fields.
                    </Text>
                  </Alert>
                )
              )
            })()}
          </GridItem>
        </SimpleGrid>
        <Flex mt={{ base: 1, md: 4 }} width="100%">
          <Button
            size={{ base: "sm", md: "sm", lg: "md" }}
            ml="auto"
            variant="primary"
            type="submit"
            text="Create schema"
            float={"right"}
            isDisabled={(() => {
              const { maxFieldsPerSchema } = getPlanLimits(currentOrg)
              return (
                maxFieldsPerSchema !== null && fieldCount > maxFieldsPerSchema
              )
            })()}
          >
            Create
          </Button>
        </Flex>
        <Stack gap={{ base: 1, md: 2, lg: 4 }} mt={{ base: 1, md: 4 }}>
          <Alert
            status="info"
            flexDirection="column"
            alignItems={"flex-start"}
            p={{ base: 2, md: 4, lg: 4 }}
          >
            <Stack flexDirection="row">
              <AlertIcon />
              <AlertTitle fontSize={{ base: "sm", md: "md", lg: "lg" }}>
                Blind Insight currently supports JSON Schema format
              </AlertTitle>
            </Stack>
            <UnorderedList fontSize={{ base: "sm", md: "md", lg: "lg" }}>
              <ListItem>
                Use the sample schema above as a guide. Learn more in the{" "}
                <Link
                  textDecoration="underline"
                  href="https://docs.blindinsight.io/overview/what-is-blindinsight/#schemas"
                  isExternal
                >
                  Schema documentation
                </Link>
              </ListItem>
              <ListItem>
                Schemas must be in{" "}
                <Link
                  textDecoration="underline"
                  href="https://json-schema.org/understanding-json-schema/reference"
                  isExternal
                >
                  JSON Schema
                </Link>{" "}
                format and consist of fields with <code>string</code> or{" "}
                <code>number</code> types.
              </ListItem>
              <ListItem>
                Each number field must include values for it&apos;s{" "}
                <code>minimum</code> and <code>maximum</code> bounds.
              </ListItem>
            </UnorderedList>{" "}
          </Alert>
        </Stack>
      </Form>
    </Flex>
  )
}
